import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Table from "../../core/pagination/datatable";
import CommonFooter from "../../core/common/footer/commonFooter";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import {
  downloadPurchaseOrderReportExcel,
  downloadPurchaseOrderReportPdf,
  getPurchaseOrderReport,
  mapPurchaseOrderReportRow,
} from "../../api/purchaseOrderReportApi";

const PurchaseOrderReport = () => {
  const t = useCallback(
    (key) => (window?.translate ? window.translate(key) : key),
    []
  );
  const getCurrentLang = useCallback(() => {
    const candidates = [
      localStorage.getItem("lang"),
      localStorage.getItem("language"),
      localStorage.getItem("i18n_lang"),
      localStorage.getItem("i18nextLng"),
      typeof window !== "undefined" ? window.lang : "",
      typeof document !== "undefined" ? document.documentElement.lang : "",
      typeof navigator !== "undefined" ? navigator.language : "",
    ];

    const hasArabic = candidates.some((candidate) =>
      String(candidate || "")
        .toLowerCase()
        .startsWith("ar")
    );

    if (hasArabic) return "ar";

    if (
      typeof document !== "undefined" &&
      String(document.documentElement?.dir || "").toLowerCase() === "rtl"
    ) {
      return "ar";
    }

    return "en";
  }, []);

  const [currentLang, setCurrentLang] = useState(() => getCurrentLang());
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    totalProducts: 0,
    totalPurchaseAmount: 0,
    totalPurchaseQty: 0,
    totalInstockQty: 0,
  });
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    type: "success",
    message: "",
  });

  const showToast = (message, type = "success") => {
    setToast({ show: true, type, message });
    window.clearTimeout(window.__purchaseOrderReportToastTimer);
    window.__purchaseOrderReportToastTimer = window.setTimeout(() => {
      setToast({ show: false, type: "success", message: "" });
    }, 2500);
  };

  const fetchReport = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      const response = await getPurchaseOrderReport({
        ...params,
        page: 1,
        limit: 100,
      });

      const tableRows = Array.isArray(response?.rows)
        ? response.rows.map((item) => mapPurchaseOrderReportRow(item))
        : [];

      setRows(tableRows);
      setSummary(
        response?.summary || {
          totalProducts: 0,
          totalPurchaseAmount: 0,
          totalPurchaseQty: 0,
          totalInstockQty: 0,
        }
      );
    } catch (error) {
      console.error(error);
      setRows([]);
      setSummary({
        totalProducts: 0,
        totalPurchaseAmount: 0,
        totalPurchaseQty: 0,
        totalInstockQty: 0,
      });
      showToast(error?.message || t("Failed to fetch purchase order report"), "error");
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const syncLang = () => setCurrentLang(getCurrentLang());
    window.addEventListener("languageChanged", syncLang);
    window.addEventListener("storage", syncLang);

    return () => {
      window.removeEventListener("languageChanged", syncLang);
      window.removeEventListener("storage", syncLang);
    };
  }, [getCurrentLang]);

  useEffect(() => {
    fetchReport(appliedSearch ? { search: appliedSearch } : {});
  }, [appliedSearch, currentLang, fetchReport]);

  const handleRefresh = () => {
    fetchReport(appliedSearch ? { search: appliedSearch } : {});
  };

  const handleSearch = () => {
    const value = search.trim();
    setAppliedSearch(value);
    fetchReport(value ? { search: value } : {});
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloadingExcel(true);
      await downloadPurchaseOrderReportExcel(
        appliedSearch ? { search: appliedSearch } : {}
      );
      showToast(t("Excel downloaded successfully"), "success");
    } catch (error) {
      console.error(error);
      showToast(error?.message || t("Failed to download excel"), "error");
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      await downloadPurchaseOrderReportPdf(
        appliedSearch ? { search: appliedSearch } : {}
      );
      showToast(t("PDF downloaded successfully"), "success");
    } catch (error) {
      console.error(error);
      showToast(error?.message || t("Failed to download pdf"), "error");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const summaryCards = useMemo(() => {
    return [
      {
        title: t("Total Products"),
        value: Number(summary?.totalProducts || 0),
        icon: "ti ti-box",
        bgClass: "bg-primary-transparent",
        textClass: "text-primary",
      },
      {
        title: t("Total Purchase Amount"),
        value: `$${Number(summary?.totalPurchaseAmount || 0).toFixed(2)}`,
        icon: "ti ti-currency-dollar",
        bgClass: "bg-success-transparent",
        textClass: "text-success",
      },
      {
        title: t("Total Purchase QTY"),
        value: Number(summary?.totalPurchaseQty || 0),
        icon: "ti ti-shopping-cart",
        bgClass: "bg-warning-transparent",
        textClass: "text-warning",
      },
      {
        title: t("Total Instock QTY"),
        value: Number(summary?.totalInstockQty || 0),
        icon: "ti ti-stack-2",
        bgClass: "bg-info-transparent",
        textClass: "text-info",
      },
    ];
  }, [summary, t]);

  const columns = [
    {
      title: t("Product Name"),
      dataIndex: "productName",
      className: "d-flex align-items-center p-3 px-2",
      render: (text, data) => (
        <>
          <Link to="#" className="avatar avatar-md me-2">
            <img
              src={
                data.img ||
                "https://via.placeholder.com/40x40.png?text=No+Image"
              }
              alt={t("Product")}
              onError={(e) => {
                e.currentTarget.src =
                  "https://via.placeholder.com/40x40.png?text=No+Image";
              }}
            />
          </Link>
          <div>
            <div className="fw-medium">{text}</div>
            {data?.sku || data?.itemCode ? (
              <div className="fs-12 text-muted">
                {data?.sku ? `${t("SKU")}: ${data.sku}` : ""}
                {data?.sku && data?.itemCode ? " | " : ""}
                {data?.itemCode ? `${t("Code")}: ${data.itemCode}` : ""}
              </div>
            ) : null}
          </div>
        </>
      ),
      sorter: (a, b) => (a.productName || "").localeCompare(b.productName || ""),
    },
    {
      title: t("Product Amount"),
      dataIndex: "productAmountLabel",
      render: (_, data) => (
        <span className="fw-semibold text-gray-9">
          ${Number(data.productAmount || 0).toFixed(2)}
        </span>
      ),
      sorter: (a, b) => Number(a.productAmount || 0) - Number(b.productAmount || 0),
    },
    {
      title: t("Product QTY"),
      dataIndex: "productQty",
      render: (text) => <span>{Number(text || 0)}</span>,
      sorter: (a, b) => Number(a.productQty || 0) - Number(b.productQty || 0),
    },
    {
      title: t("Instock QTY"),
      dataIndex: "instockQty",
      render: (text, row) => (
        <span
          className={`badge ${
            Number(row?.instockQty || 0) > 0
              ? "bg-success-transparent text-success"
              : "bg-danger-transparent text-danger"
          }`}
        >
          {Number(text || 0)}
        </span>
      ),
      sorter: (a, b) => Number(a.instockQty || 0) - Number(b.instockQty || 0),
    },
  ];

  return (
    <div>
      {toast.show ? (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            zIndex: 99999,
            minWidth: "280px",
            maxWidth: "420px",
            background: toast.type === "success" ? "#28c76f" : "#ea5455",
            color: "#fff",
            padding: "14px 16px",
            borderRadius: "10px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            fontWeight: 500,
          }}
        >
          <div className="d-flex justify-content-between align-items-center gap-3">
            <span>{toast.message}</span>
            <button
              type="button"
              onClick={() =>
                setToast({ show: false, type: "success", message: "" })
              }
              style={{
                border: "none",
                background: "transparent",
                color: "#fff",
                fontSize: "18px",
                lineHeight: 1,
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>
        </div>
      ) : null}

      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>{t("Purchase order")}</h4>
                <h6>{t("Manage Your Purchase order")}</h6>
              </div>
            </div>

            <ul className="table-top-head">
              <TooltipIcons />
              <li>
                <Link to="#" onClick={handleRefresh}>
                  <RefreshIcon />
                </Link>
              </li>
              <CollapesIcon />
            </ul>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-success"
                onClick={handleDownloadExcel}
                disabled={downloadingExcel}
              >
                <i className="ti ti-file-spreadsheet me-1" />
                {downloadingExcel ? t("Downloading...") : t("Download Excel")}
              </button>

              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
              >
                <i className="ti ti-file-type-pdf me-1" />
                {downloadingPdf ? t("Downloading...") : t("Download PDF")}
              </button>
            </div>
          </div>

          <div className="row">
            {summaryCards.map((card) => (
              <div className="col-xl-3 col-md-6 col-sm-12 d-flex" key={card.title}>
                <div className="card flex-fill">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <p className="mb-1 text-muted">{card.title}</p>
                        <h4 className="mb-0">{card.value}</h4>
                      </div>
                      <span
                        className={`avatar avatar-lg rounded-circle ${card.bgClass}`}
                      >
                        <i className={`${card.icon} ${card.textClass} fs-20`} />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("Search by product name, sku, item code...")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                  />
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSearch}
                >
                  <i className="ti ti-search me-1" />
                  {t("Search")}
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setSearch("");
                    setAppliedSearch("");
                    fetchReport();
                  }}
                >
                  <i className="ti ti-rotate me-1" />
                  {t("Reset")}
                </button>

                {loading ? (
                  <span className="badge bg-info-transparent text-info">
                    {t("Loading...")}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="card-body">
              <div className="table-responsive">
                <Table columns={columns} dataSource={rows} />
              </div>
            </div>
          </div>
        </div>

        <CommonFooter />
      </div>
    </div>
  );
};

export default PurchaseOrderReport;
